import { Link, useLocation, useNavigate } from "react-router-dom";
import { Instagram, Youtube, Linkedin, Podcast } from "lucide-react";
import { XIcon } from "@/lib/socialPlatforms";
import { usePodgoVersion } from "@/hooks/usePodgoVersion";
import { useBugReport } from "@/contexts/BugReportContext";
import { useNavMenu } from "@/hooks/useNavMenu";
import { NavMenuLink } from "@/components/marketing/NavMenuLink";
import { BrandLogo } from "@/components/marketing/BrandLogo";

// Column count drives the desktop grid; static classes keep Tailwind's JIT happy.
const COLUMN_GRID: Record<number, string> = {
  1: "md:grid-cols-2",
  2: "md:grid-cols-3",
  3: "md:grid-cols-4",
  4: "md:grid-cols-5",
  5: "md:grid-cols-6",
};


const socialLinks = [
  { label: "YouTube", href: "https://youtube.com/@podgopodcasts", icon: Youtube },
  { label: "X", href: "https://x.com/podgoservices", icon: XIcon },
  { label: "Instagram", href: "https://www.instagram.com/podgoservices", icon: Instagram },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/podgo-io/", icon: Linkedin },
];

export function MarketingFooter() {
  const { version } = usePodgoVersion();
  const location = useLocation();
  const navigate = useNavigate();
  const { launch: launchBugReport } = useBugReport();
  const { menus } = useNavMenu("footer");

  const handleHashLink = (href: string) => {
    if (!href.startsWith("#")) return;
    if (location.pathname === "/") {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + href);
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 400);
    }
  };

  const handleFooterNavigate = (href: string) => {
    if (href.startsWith("#") || href.startsWith("/#")) {
      handleHashLink(href.replace(/^\//, ""));
      return;
    }
    navigate(href);
    window.scrollTo({ top: 0 });
  };

  return (
    <footer className="bg-primary border-t border-secondary/10">
      <div className="container mx-auto px-4 md:px-6 py-12 md:py-16">
        {/* Main footer content */}
        <div className={`grid grid-cols-2 ${COLUMN_GRID[menus.length] ?? "md:grid-cols-5"} gap-8 md:gap-12 mb-12`}>

          {/* Brand column */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4" aria-label="PODGO home">
              <BrandLogo className="h-8 w-auto" decorative>
                <>
                  <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
                    <Podcast className="h-4 w-4 text-accent-foreground" aria-hidden="true" />
                  </div>
                  <span className="text-xl font-bold text-secondary">PODGO</span>
                </>
              </BrandLogo>
            </Link>
            <p className="text-secondary/80 text-sm leading-relaxed mb-4">
              The unified podcast operations platform. Distribute, track, and grow your podcast everywhere.
            </p>
            {/* Social links */}
            <div className="flex gap-4">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center text-secondary/80 hover:bg-secondary/20 hover:text-secondary transition-colors"
                    aria-label={`${social.label} (opens in new tab)`}
                  >
                    <Icon className="w-4 h-4" aria-hidden="true" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Link columns */}
          {menus.map((section) => (
            <div key={section.id}>
              <p className="font-semibold text-secondary mb-4 text-sm">
                {section.title}
              </p>
              <ul className="space-y-3">
                {section.items.map((link) => (
                  <li key={link.id}>
                    <NavMenuLink
                      item={link}
                      className="text-secondary/80 hover:text-secondary text-sm transition-colors text-left"
                      onAction={(key) => key === "bug_report" && launchBugReport()}
                      onInternalNavigate={handleFooterNavigate}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}

        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-secondary/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <p className="text-secondary/70 text-sm">
              © {new Date().getFullYear()} PODGO. All rights reserved
            </p>
            <p className="text-secondary/80 text-xs mt-1">
              PODGO Beta v{version}
            </p>
          </div>
          <p className="text-secondary/80 text-xs">
            Made with ❤️ for podcasters everywhere
          </p>
        </div>
      </div>
    </footer>
  );
}
